import { useMemo } from 'react';
import { useMeetingStore } from '../../stores/meetingStore';
import { useAuthStore } from '../../stores/authStore';
import { useUserProfiles } from '../../hooks/useUserProfiles';
import type { Participant } from '../../types';
import { VideoTile } from './VideoTile';

/**
 * Grid of participant video tiles.
 */
export function VideoGrid() {
  const { participants, localParticipant, spotlightParticipantId, setSpotlight } =
    useMeetingStore();
  const { user } = useAuthStore();

  const allParticipants = localParticipant
    ? [
        localParticipant,
        ...participants.filter((p) => p.participantId !== localParticipant.participantId),
      ]
    : participants;
  const profileMap = useUserProfiles(allParticipants.map((participant) => participant.participantId));
  const resolvedParticipants = useMemo(() => {
    return allParticipants
      .map((participant) => applyProfile(participant, profileMap))
      .map((participant) => applyLocalAvatar(participant, user));
  }, [allParticipants, profileMap, user]);

  const spotlightParticipant = spotlightParticipantId
    ? resolvedParticipants.find((p) => p.participantId === spotlightParticipantId)
    : null;

  // Spotlight mode: main video fills space, others in sidebar
  if (spotlightParticipant) {
    const others = resolvedParticipants.filter(
      (p) => p.participantId !== spotlightParticipantId
    );

    return (
      <div className="flex gap-3 h-full items-center justify-center">
        <VideoTile
          participant={spotlightParticipant}
          isLocal={spotlightParticipant.participantId === localParticipant?.participantId}
          isSpotlight
          onSpotlight={() => setSpotlight(null)}
        />
        {others.length > 0 && (
          <div className="flex flex-col gap-2">
            {others.map((participant) => (
              <div key={participant.participantId} className="w-24">
                <VideoTile
                  participant={participant}
                  isLocal={participant.participantId === localParticipant?.participantId}
                  onSpotlight={() => setSpotlight(participant.participantId)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Normal grid mode - tiles fill available space
  const gridCols = getGridCols(resolvedParticipants.length);

  return (
    <div className={`flex flex-wrap gap-4 justify-center items-start content-start`}>
      {resolvedParticipants.map((participant, index) => (
        <div
          key={participant.participantId}
          className={`${gridCols}`}
        >
          <VideoTile
            participant={participant}
            isLocal={index === 0 && !!localParticipant}
            onSpotlight={() => setSpotlight(participant.participantId)}
          />
        </div>
      ))}
    </div>
  );
}

/**
 * Overrides participant display data with profile information when available.
 */
function applyProfile(
  participant: Participant,
  profileMap: Record<string, { displayName?: string; avatarUrl?: string } | undefined>
): Participant {
  const profile = profileMap[participant.participantId];
  if (!profile) {
    return participant;
  }

  return {
    ...participant,
    displayName: profile.displayName ?? participant.displayName,
    avatarUrl: profile.avatarUrl ?? participant.avatarUrl,
  };
}

function applyLocalAvatar(
  participant: Participant,
  user: { uid: string; avatarUrl?: string } | null
): Participant {
  if (!user || participant.participantId !== user.uid) {
    return participant;
  }

  return {
    ...participant,
    avatarUrl: user.avatarUrl ?? participant.avatarUrl,
  };
}

function getGridCols(count: number): string {
  // Use flex-basis to control tile width based on participant count
  if (count <= 1) return 'w-[min(90vw,500px)]';
  if (count <= 2) return 'w-[min(45vw,400px)]';
  if (count <= 4) return 'w-[min(45vw,320px)]';
  if (count <= 6) return 'w-[min(30vw,280px)]';
  if (count <= 9) return 'w-[min(30vw,240px)]';
  return 'w-[min(23vw,200px)]';
}
